// /reports/demand-forecast — Demand Forecast (PAGES.md §3.105)
//
// Demand forecasting dashboard for festival and daily operations. Predicts
// required quantities per product based on historical sales, current bookings,
// and trend analysis. Shows predicted vs last year, stock levels, shortfall,
// raw material requirements, and auto-PO generation.
// RESTAURANT vertical only — MANAGER+ (level 30). Feature flag: ENABLE_FESTIVE_MODULE.
// Traceability: FR-FEST-020, FR-FEST-021
// User Story: US-FEST-010 (Festive Demand Forecasting)

import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Badge, BarChart, Button, Select, Alert, DatePicker } from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import { useDashboardStore } from '@/stores/use-dashboard-store';
import { useAuthStore } from '@/stores/use-auth-store';
import { GLASS, cn } from '@/constants/design-tokens';
import { festiveService } from '@/services/festive.service';
import { ROLE_LEVELS } from '@/types/user';
import {
  ReportPageShell,
  ReportKpiRow,
  ReportTable,
  ReportExportMenu,
  ReportSkeleton,
  ReportError,
  reportsApi,
  downloadCsv,
  shareReportLink,
  KpiIcons,
} from '@/features/reports';
import type { DemandForecastProductRow, DemandForecastMaterialRow } from '@/features/reports';

// ─── Constants ─────────────────────────────────────────────────────────────

type ProductRow = DemandForecastProductRow & { id: string };
type MaterialRow = DemandForecastMaterialRow & { id: string };

const CONFIDENCE_VARIANT: Record<'HIGH' | 'MEDIUM' | 'LOW', 'success' | 'warning' | 'danger'> = {
  HIGH: 'success',
  MEDIUM: 'warning',
  LOW: 'danger',
};

// ─── Component ──────────────────────────────────────────────────────────────

export function DemandForecastPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const selectedLocationId = useDashboardStore((s) => s.selectedLocationId);

  // Role-based access
  const profile = useAuthStore((s) => s.profile);
  const roleLevel = ROLE_LEVELS[profile?.role ?? ''] ?? 0;
  const canGeneratePO = roleLevel >= 50; // OWNER+

  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [festivalId, setFestivalId] = useState<string | undefined>(undefined);

  useEffect(() => { document.title = 'Demand Forecast — Reports — Orio360 Admin'; }, []);

  // Fetch festivals for the selector
  const { data: festivalsData } = useQuery({
    queryKey: ['festivals-for-forecast'],
    queryFn: () => festiveService.listFestivals(),
    staleTime: 60 * 60 * 1000,
  });

  const festivalOptions = useMemo(() => {
    const opts: { value: string; label: string }[] = [
      { value: '', label: 'Daily Forecast' },
    ];
    if (festivalsData?.data) {
      for (const f of festivalsData.data) {
        opts.push({ value: f.id, label: f.name });
      }
    }
    return opts;
  }, [festivalsData]);

  // Fetch forecast data
  const { data, isLoading, error } = useQuery({
    queryKey: ['reports', 'demand-forecast', date, festivalId, selectedLocationId],
    queryFn: () => reportsApi.getDemandForecast({
      date,
      festival_id: festivalId,
      location_id: selectedLocationId,
    }),
    staleTime: 60_000,
  });

  // Auto-generate PO mutation
  const autoPoMutation = useMutation({
    mutationFn: () =>
      reportsApi.autoGeneratePO({ location_id: selectedLocationId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reports', 'demand-forecast'] });
    },
  });

  // Reset stale PO success state when filters change
  useEffect(() => {
    if (autoPoMutation.isSuccess || autoPoMutation.isError) autoPoMutation.reset();
    // Deps are the filters only, on purpose: reset on filter change, not on every mutation update.
  }, [date, festivalId, selectedLocationId]);

  // Transform data
  const products: ProductRow[] = useMemo(
    () => (data?.products ?? []).map((p) => ({ ...p, id: p.product_id })),
    [data],
  );
  const materials: MaterialRow[] = useMemo(
    () => (data?.materials ?? []).map((m) => ({ ...m, id: m.ingredient_id })),
    [data],
  );

  const hasShortfallMaterials = materials.some((m) => m.shortfall > 0);

  // Keyboard shortcut: g = Auto-Generate PO (PAGES.md §3.105)
  const handleAutoGeneratePO = useCallback(() => {
    if (!canGeneratePO || autoPoMutation.isPending || !hasShortfallMaterials) return;
    autoPoMutation.mutate();
  }, [canGeneratePO, autoPoMutation, hasShortfallMaterials]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;
      if (e.key === 'g') { e.preventDefault(); handleAutoGeneratePO(); }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleAutoGeneratePO]);

  // Chart data: predicted qty for top 10 products
  // NOTE: @bbpos/ui BarChart supports single-series only — showing predicted qty.
  // Predicted vs last year comparison is available in the product table below.
  const chartData = useMemo(() =>
    products.slice(0, 10).map((p) => ({
      label: p.product_name.length > 14 ? `${p.product_name.slice(0, 14)}...` : p.product_name,
      value: p.predicted_qty,
    })),
  [products]);

  // Detect insufficient data
  const isLowConfidence = data?.confidence === 'LOW' && data.data_years_available < 2;

  // Product table columns (all spec columns including Action)
  const productCols: ColumnDef<ProductRow>[] = useMemo(() => [
    {
      key: 'product_name', header: 'Product',
      render: (_v: unknown, row: ProductRow) => (
        <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{row.product_name}</span>
      ),
    },
    {
      key: 'predicted_qty', header: 'Predicted', numeric: true,
      render: (_v: unknown, row: ProductRow) => (
        <span className="text-sm font-semibold font-mono tabular-nums text-neutral-900 dark:text-neutral-100">
          {row.predicted_qty.toLocaleString('en-IN')}
        </span>
      ),
    },
    {
      key: 'last_year_qty', header: 'Last Year', numeric: true,
      render: (_v: unknown, row: ProductRow) => (
        <span className="font-mono tabular-nums text-neutral-500 dark:text-neutral-400">
          {row.last_year_qty.toLocaleString('en-IN')}
        </span>
      ),
    },
    {
      key: 'growth_pct', header: 'Growth', numeric: true,
      render: (_v: unknown, row: ProductRow) => {
        const cls = row.growth_pct >= 0 ? 'text-success-600 dark:text-success-400' : 'text-danger-600 dark:text-danger-400';
        return (
          <span className={`font-mono tabular-nums font-semibold ${cls}`}>
            {row.growth_pct >= 0 ? '+' : ''}{row.growth_pct.toFixed(1)}%
          </span>
        );
      },
    },
    {
      key: 'current_stock', header: 'Stock', numeric: true,
      render: (_v: unknown, row: ProductRow) => (
        <span className="font-mono tabular-nums text-neutral-700 dark:text-neutral-300">
          {row.current_stock.toLocaleString('en-IN')}
        </span>
      ),
    },
    {
      key: 'shortfall', header: 'Shortfall', numeric: true,
      render: (_v: unknown, row: ProductRow) => (
        row.shortfall > 0
          ? <Badge variant="danger" size="sm">-{row.shortfall.toLocaleString('en-IN')}</Badge>
          : <Badge variant="success" size="sm">Surplus</Badge>
      ),
    },
    {
      key: 'confidence', header: 'Confidence',
      render: (_v: unknown, row: ProductRow) => (
        <Badge variant={CONFIDENCE_VARIANT[row.confidence]} size="sm">{row.confidence}</Badge>
      ),
    },
    {
      key: 'action', header: 'Action',
      render: (_v: unknown, _row: ProductRow) => (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); navigate('/production-planning'); }}
          className="text-xs font-medium text-violet-600 dark:text-violet-400 hover:text-violet-800 dark:hover:text-violet-200 transition-colors duration-200"
          aria-label="View production plan"
        >
          View Plan
        </button>
      ),
    },
  ], [navigate]);

  // Material table columns
  const materialCols: ColumnDef<MaterialRow>[] = useMemo(() => [
    {
      key: 'ingredient_name', header: 'Ingredient',
      render: (_v: unknown, row: MaterialRow) => (
        <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{row.ingredient_name}</span>
      ),
    },
    {
      key: 'required_qty', header: 'Required', numeric: true,
      render: (_v: unknown, row: MaterialRow) => (
        <span className="font-mono tabular-nums text-neutral-700 dark:text-neutral-300">
          {row.required_qty.toLocaleString('en-IN')} {row.unit}
        </span>
      ),
    },
    {
      key: 'in_stock', header: 'In Stock', numeric: true,
      render: (_v: unknown, row: MaterialRow) => (
        <span className={`font-mono tabular-nums font-medium ${row.in_stock >= row.required_qty ? 'text-success-600 dark:text-success-400' : 'text-danger-600 dark:text-danger-400'}`}>
          {row.in_stock.toLocaleString('en-IN')}
        </span>
      ),
    },
    {
      key: 'shortfall', header: 'Shortfall', numeric: true,
      render: (_v: unknown, row: MaterialRow) => (
        row.shortfall > 0
          ? <Badge variant="danger" size="sm">-{row.shortfall.toLocaleString('en-IN')}</Badge>
          : <Badge variant="success" size="sm">OK</Badge>
      ),
    },
    {
      key: 'suggested_po_qty', header: 'Suggested PO', numeric: true,
      render: (_v: unknown, row: MaterialRow) => (
        <span className="text-sm font-semibold text-brand-600 dark:text-brand-300 font-mono tabular-nums">
          {row.suggested_po_qty.toLocaleString('en-IN')}
        </span>
      ),
    },
  ], []);

  // Export handler
  const handleExport = useCallback((fmt: 'csv' | 'pdf' | 'whatsapp' | 'email') => {
    if (fmt === 'pdf') {
      // Browser print dialog -> "Save as PDF"; no server-side PDF renderer exists.
      window.print();
      return;
    }
    if (fmt === 'csv') {
      downloadCsv(
        `demand-forecast-${date}.csv`,
        [
          { header: 'Product', get: (r: ProductRow) => r.product_name },
          { header: 'Predicted', get: (r: ProductRow) => r.predicted_qty },
          { header: 'Last Year', get: (r: ProductRow) => r.last_year_qty },
          { header: 'Growth %', get: (r: ProductRow) => r.growth_pct.toFixed(1) },
          { header: 'Stock', get: (r: ProductRow) => r.current_stock },
          { header: 'Shortfall', get: (r: ProductRow) => r.shortfall },
          { header: 'Confidence', get: (r: ProductRow) => r.confidence },
        ],
        products,
      );
      return;
    }
    if (fmt === 'whatsapp' || fmt === 'email') {
      shareReportLink(
        fmt,
        'Orio360 Demand Forecast',
        `${data?.items_with_shortfall ?? 0} items with shortfall | Confidence: ${data?.confidence ?? 'LOW'}`,
      );
    }
  }, [date, products, data]);

  // Mobile product card
  const renderProductMobileCard = useCallback((row: ProductRow) => (
    <div
      key={row.id}
      className={cn(GLASS, 'p-4 animate-slide-up')}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{row.product_name}</span>
        <Badge variant={CONFIDENCE_VARIANT[row.confidence]} size="sm">{row.confidence}</Badge>
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
        <span className="text-neutral-500 dark:text-neutral-400">Predicted</span>
        <span className="font-mono tabular-nums font-semibold text-neutral-900 dark:text-neutral-100 text-right">
          {row.predicted_qty.toLocaleString('en-IN')}
        </span>
        <span className="text-neutral-500 dark:text-neutral-400">Last Year</span>
        <span className="font-mono tabular-nums text-neutral-500 dark:text-neutral-400 text-right">
          {row.last_year_qty.toLocaleString('en-IN')}
        </span>
        <span className="text-neutral-500 dark:text-neutral-400">Growth</span>
        <span className={`font-mono tabular-nums font-semibold text-right ${row.growth_pct >= 0 ? 'text-success-600 dark:text-success-400' : 'text-danger-600 dark:text-danger-400'}`}>
          {row.growth_pct >= 0 ? '+' : ''}{row.growth_pct.toFixed(1)}%
        </span>
        <span className="text-neutral-500 dark:text-neutral-400">Stock</span>
        <span className="font-mono tabular-nums text-neutral-700 dark:text-neutral-300 text-right">
          {row.current_stock.toLocaleString('en-IN')}
        </span>
      </div>
      <div className="flex items-center justify-between mt-2 pt-2">
        {row.shortfall > 0 ? (
          <Badge variant="danger" size="sm">Shortfall: {row.shortfall.toLocaleString('en-IN')}</Badge>
        ) : (
          <Badge variant="success" size="sm">Surplus</Badge>
        )}
        <button
          type="button"
          onClick={() => navigate('/production-planning')}
          className="text-xs font-medium text-violet-600 dark:text-violet-400"
          aria-label="View production plan"
        >
          View Plan
        </button>
      </div>
    </div>
  ), [navigate]);

  return (
    <ReportPageShell
      title="Demand Forecast"
      subtitle="Predicted demand per product with raw material requirements, historical comparison, and auto-PO suggestions."
      breadcrumbs={[
        { label: 'Home', href: '/home' },
        { label: 'Reports', href: '/reports' },
        { label: 'Demand Forecast' },
      ]}
      accent="violet-pink"
      headerActions={<ReportExportMenu onExport={handleExport} />}
      filterBar={
        <div className={cn(GLASS, 'p-5')}>
          <div className="flex flex-col sm:flex-row sm:items-end gap-4">
            <div className="w-full sm:w-56">
              <label className="block text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-2">
                Forecast Type
              </label>
              <Select
                options={festivalOptions}
                value={festivalId ?? ''}
                onChange={(v) => {
                  const val = typeof v === 'string' ? v : v[0] ?? '';
                  setFestivalId(val || undefined);
                }}
                placeholder="Select forecast type"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-2">
                Forecast Date
              </label>
              <DatePicker
                value={date ? new Date(date + 'T00:00:00') : undefined}
                onChange={(d: Date) => setDate(d.toISOString().split('T')[0])}
              />
            </div>
          </div>
        </div>
      }
    >
      {isLoading ? (
        <ReportSkeleton kpiCount={4} />
      ) : error ? (
        <ReportError />
      ) : (
        <>
          {/* FORECAST_INSUFFICIENT_DATA alert */}
          {isLowConfidence && (
            <div className="mb-6 animate-slide-up">
              <Alert
                variant="warning"
                title="Limited historical data"
                description="Forecast confidence is LOW. Forecasts require at least two years of sales data for accurate predictions. Keep recording sales to improve accuracy."
              />
            </div>
          )}

          {/* KPI Row */}
          <ReportKpiRow
            tiles={[
              {
                id: 'total',
                title: 'Total Products',
                value: (data?.total_products ?? 0).toLocaleString('en-IN'),
                tone: 'indigo',
                icon: KpiIcons.tag,
              },
              {
                id: 'shortfall',
                title: 'Items w/ Shortfall',
                value: (data?.items_with_shortfall ?? 0).toLocaleString('en-IN'),
                tone: 'danger',
                icon: KpiIcons.trendDown,
              },
              {
                id: 'conf',
                title: 'Confidence',
                value: data?.confidence ?? 'LOW',
                tone: data?.confidence === 'HIGH' ? 'success' : data?.confidence === 'MEDIUM' ? 'warning' : 'danger',
                icon: KpiIcons.star,
              },
              {
                id: 'years',
                title: 'Data Years',
                value: String(data?.data_years_available ?? 0),
                tone: 'violet',
                icon: KpiIcons.clock,
              },
            ]}
          />

          {/* Bar Chart: Predicted qty (top 10 products) */}
          {chartData.length > 0 && (
            <div className={cn(GLASS, 'p-5 mb-6 animate-slide-up')} role="img" aria-label="Bar chart showing predicted quantity for top 10 products">
              <BarChart
                data={chartData}
                title="Predicted Quantity (top 10 products)"
              />
            </div>
          )}

          {/* Desktop: Product Forecast Table */}
          <div className="hidden sm:block">
            <ReportTable
              title="Product Forecast"
              columns={productCols}
              data={products}
              emptyTitle="Not enough data for forecasting"
              emptyDescription="Forecasts require at least one year of sales data. Keep recording sales to enable predictions."
              totalCount={products.length}
            />
          </div>

          {/* Mobile: Product Forecast Cards (xs-sm) */}
          <div className="block sm:hidden space-y-3 mb-6">
            {products.length === 0 ? (
              <div className="text-center py-12">
                <svg className="h-14 w-14 mx-auto text-neutral-300 dark:text-neutral-600 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
                <p className="text-sm font-medium text-neutral-500 dark:text-neutral-400">Not enough data for forecasting</p>
                <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-1">Keep recording sales to enable predictions.</p>
              </div>
            ) : (
              <>
                <h3 className="text-sm font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider px-1">
                  Product Forecast ({products.length})
                </h3>
                {/* Horizontal scroll KPIs on mobile */}
                <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-hide">
                  {[
                    { label: 'Products', value: (data?.total_products ?? 0).toLocaleString('en-IN') },
                    { label: 'Shortfall', value: (data?.items_with_shortfall ?? 0).toLocaleString('en-IN') },
                    { label: 'Confidence', value: data?.confidence ?? 'LOW' },
                    { label: 'Data Years', value: String(data?.data_years_available ?? 0) },
                  ].map((kpi) => (
                    <div key={kpi.label} className={cn('flex-shrink-0 px-4 py-3 min-w-[120px]', GLASS)}>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400">{kpi.label}</p>
                      <p className="text-lg font-bold font-mono tabular-nums text-neutral-900 dark:text-neutral-100">{kpi.value}</p>
                    </div>
                  ))}
                </div>
                {products.map(renderProductMobileCard)}
              </>
            )}
          </div>

          {/* Raw Material Requirements Table */}
          <ReportTable
            title="Raw Material Requirements"
            columns={materialCols}
            data={materials}
            emptyTitle="No material requirements to show"
            totalCount={materials.length}
          />

          {/* Auto-Generate PO Button (OWNER+ only) */}
          {canGeneratePO && hasShortfallMaterials && (
            <div className="flex justify-end mb-6 animate-slide-up">
              <Button
                variant="primary"
                loading={autoPoMutation.isPending}
                onClick={handleAutoGeneratePO}
                aria-label="Auto-generate purchase order for shortfall items (shortcut: g)"
              >
                Auto-Generate Purchase Order
              </Button>
            </div>
          )}

          {/* PO success feedback */}
          {autoPoMutation.isSuccess && autoPoMutation.data?.po_number && (
            <div className="mb-6 animate-slide-up">
              <Alert
                variant="success"
                title={`Purchase order ${autoPoMutation.data.po_number} generated`}
                description="A draft purchase order has been created for the low-stock items at this location. Check the procurement section for details."
              />
            </div>
          )}
          {autoPoMutation.isError && (
            <div className="mb-6 animate-slide-up">
              <Alert
                variant="danger"
                title="Purchase order not generated"
                description={autoPoMutation.error.message}
              />
            </div>
          )}
        </>
      )}
    </ReportPageShell>
  );
}
